/**
 * 缓存工具类
 * 支持 localStorage 和 sessionStorage 缓存
 */
class CacheUtil {
  /**
   * 设置缓存
   * @param {string} key 缓存键名
   * @param {any} value 缓存值
   * @param {number} expire 过期时间（秒），默认1小时
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   */
  static set(key, value, expire = 3600, storage = 'local') {
    const storageObj = storage === 'session' ? sessionStorage : localStorage;
    const cacheData = {
      value,
      expire: expire ? Date.now() + expire * 1000 : null,
      timestamp: Date.now()
    };
      
    try {
      storageObj.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('缓存设置失败:', error);
      // 如果存储空间不足，清理过期缓存后重试
      this.clearExpired(storage);
      try {
        storageObj.setItem(key, JSON.stringify(cacheData));
      } catch (retryError) {
        console.error('缓存设置重试失败:', retryError);
      }
    }
  }

  /**
   * 获取缓存
   * @param {string} key 缓存键名
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   * @returns {any} 缓存值，如果不存在或已过期返回 null
   */
  static get(key, storage = 'local') {
    const storageObj = storage === 'session' ? sessionStorage : localStorage;
    
    try {
      const cacheStr = storageObj.getItem(key);
      if (!cacheStr) return null;
      
      const cacheData = JSON.parse(cacheStr);
      
      // 检查是否过期
      if (cacheData.expire && Date.now() > cacheData.expire) {
        this.remove(key, storage);
        return null;
      }
      
      return cacheData.value;
    } catch (error) {
      console.warn('缓存获取失败:', error);
      return null;
    }
  }

  /**
   * 移除缓存
   * @param {string} key 缓存键名
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   */
  static remove(key, storage = 'local') {
    const storageObj = storage === 'session' ? sessionStorage : localStorage;
    storageObj.removeItem(key);
  }

  /**
   * 清空所有缓存
   * @param {string} storage 存储类型 'local' | 'session' | 'all'，默认 'all'
   */
  static clear(storage = 'all') {
    if (storage === 'all' || storage === 'local') {
      localStorage.clear();
    }
    if (storage === 'all' || storage === 'session') {
      sessionStorage.clear();
    }
  }

  /**
   * 清理过期缓存
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   */
  static clearExpired(storage = 'local') {
    const storageObj = storage === 'session' ? sessionStorage : localStorage;
    const keys = Object.keys(storageObj);
    
    keys.forEach(key => {
      try {
        const cacheStr = storageObj.getItem(key);
        if (cacheStr) {
          const cacheData = JSON.parse(cacheStr);
          if (cacheData.expire && Date.now() > cacheData.expire) {
            storageObj.removeItem(key);
          }
        }
      } catch (error) {
        // 如果解析失败，可能是非缓存数据，跳过
      }
    });
  }

  /**
   * 检查缓存是否存在且未过期
   * @param {string} key 缓存键名
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   * @returns {boolean} 是否存在有效缓存
   */
  static has(key, storage = 'local') {
    return this.get(key, storage) !== null;
  }

  /**
   * 获取缓存信息（包含过期时间等）
   * @param {string} key 缓存键名
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   * @returns {object|null} 缓存信息
   */
  static getInfo(key, storage = 'local') {
    const storageObj = storage === 'session' ? sessionStorage : localStorage;
    
    try {
      const cacheStr = storageObj.getItem(key);
      if (!cacheStr) return null;
      
      const cacheData = JSON.parse(cacheStr);
      
      // 检查是否过期
      if (cacheData.expire && Date.now() > cacheData.expire) {
        this.remove(key, storage);
        return null;
      }
      
      return {
        value: cacheData.value,
        expire: cacheData.expire,
        timestamp: cacheData.timestamp,
        remainingTime: cacheData.expire ? Math.max(0, cacheData.expire - Date.now()) : null
      };
    } catch (error) {
      console.warn('缓存信息获取失败:', error);
      return null;
    }
  }

  /**
   * 获取所有缓存键名
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   * @returns {string[]} 缓存键名数组
   */
  static getKeys(storage = 'local') {
    const storageObj = storage === 'session' ? sessionStorage : localStorage;
    return Object.keys(storageObj);
  }

  /**
   * 获取缓存统计信息
   * @param {string} storage 存储类型 'local' | 'session'，默认 'local'
   * @returns {object} 统计信息
   */
  static getStats(storage = 'local') {
    const storageObj = storage === 'session' ? sessionStorage : localStorage;
    const keys = Object.keys(storageObj);
    let totalSize = 0;
    let validCount = 0;
    let expiredCount = 0;
    
    keys.forEach(key => {
      try {
        const cacheStr = storageObj.getItem(key);
        if (cacheStr) {
          totalSize += cacheStr.length;
          const cacheData = JSON.parse(cacheStr);
          
          if (cacheData.expire && Date.now() > cacheData.expire) {
            expiredCount++;
          } else {
            validCount++;
          }
        }
      } catch (error) {
        // 跳过非缓存数据
      }
    });
    
    return {
      totalKeys: keys.length,
      validCount,
      expiredCount,
      totalSize: `${(totalSize / 1024).toFixed(2)} KB`
    };
  }
}

export default CacheUtil; 